import React from 'react'
import { CloudBillingPlanFragment } from '@sketch/gql-types'

import Card from './components/Card'
import CardProvisionNewPlans from './components/CardProvisionNewPlans'

import { PlansWrapper } from './PlanPicker.styles'

// This is a valid use case to narrow down Component type
// eslint-disable-next-line no-restricted-imports
import { Currency } from '@sketch/gql-types/expansive'

interface PlansProps {
  isDisabled?: boolean
  currency: Currency
  selectedPlan?: CloudBillingPlanFragment
  onlyBusinessPlansAllowed: boolean
  plans: CloudBillingPlanFragment[]
  hasAutomatedPlanProvisionFFOn?: boolean
  onChange: (clickedPlan: CloudBillingPlanFragment) => void
}

const CURRENCY_BY_CURRENCY_CODE = {
  USD: {
    symbol: '$',
    code: 'USD',
  },
  EUR: {
    symbol: '€',
    code: 'EUR',
  },
}

/**
 * PlanPicker
 *
 * Renders cards with each sketch plan (Trial, Monthly, Yearly) detail
 * and allows the user to select one of them.
 *
 * Trial can be hidden for certain users using the showTrial flag
 *
 * @example
 * <PlanPicker onChange={(clickedPlan) => {...}} />
 *
 */
export const PlanPicker: React.FC<PlansProps> = ({
  currency,
  isDisabled = false,
  onChange,
  selectedPlan,
  onlyBusinessPlansAllowed,
  plans,
  hasAutomatedPlanProvisionFFOn = false,
}) => {
  const CardComponent = hasAutomatedPlanProvisionFFOn
    ? CardProvisionNewPlans
    : Card
  return (
    <PlansWrapper>
      {plans.map(plan => (
        <CardComponent
          key={plan.id}
          onChange={onChange}
          plan={plan}
          isSelected={plan.id === selectedPlan?.id}
          isDisabled={
            isDisabled ||
            (selectedPlan?.type === 'MONTHLY' && plan.product === 'BUSINESS') ||
            (onlyBusinessPlansAllowed && plan.product !== 'BUSINESS')
          }
          showAvailableOnlyOnYearlyNotice={plan.product === 'BUSINESS'}
          showAvailableOnlyOnYearlyTooltip={
            plan.product === 'BUSINESS' && selectedPlan?.type === 'MONTHLY'
          }
          currency={CURRENCY_BY_CURRENCY_CODE[currency]}
        />
      ))}
    </PlansWrapper>
  )
}
